import { useCallback, useEffect, useState } from 'react';
import { monEspaceApi } from './resources';
import { surRafraichissement } from './rafraichissement';

/**
 * La cloche de l'employé : ses signaux, relus au rythme de l'application
 * (chaque minute, au retour sur l'écran).
 *
 * <p>Les appels sont gardés : un test qui simule l'API sans ces fonctions,
 * ou un serveur plus ancien, laisse simplement la cloche vide.
 *
 * @param {boolean} actif vrai pour un compte rattaché à une fiche d'employé
 * @returns {{liste: object[], nonVus: number, relit: () => void, marqueVu: (id: number) => void,
 *   marqueTous: (types?: string[]) => void, repond: (id: number, reponse: string, texte?: string) => Promise<object>}}
 */
export function useSignaux(actif) {
  const [liste, setListe] = useState([]);

  const relit = useCallback(() => {
    if (typeof monEspaceApi.signaux !== 'function') return;
    Promise.resolve()
      .then(() => monEspaceApi.signaux())
      .then((recus) => setListe(Array.isArray(recus) ? recus : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!actif) {
      setListe([]);
      return undefined;
    }
    relit();
    return surRafraichissement(relit);
  }, [actif, relit]);

  const maintenant = () => new Date().toISOString();

  const marqueVu = useCallback((id) => {
    setListe((p) => p.map((s) => (s.id === id && !s.vuLe ? { ...s, vuLe: maintenant() } : s)));
    if (typeof monEspaceApi.signalVu === 'function') monEspaceApi.signalVu(id).catch(() => {});
  }, []);

  const marqueTous = useCallback((types) => {
    setListe((p) =>
      p.map((s) =>
        !s.vuLe && (!types || types.includes(s.type)) ? { ...s, vuLe: maintenant() } : s,
      ),
    );
    if (typeof monEspaceApi.signauxVus === 'function')
      monEspaceApi.signauxVus(types).catch(() => {});
  }, []);

  const repond = useCallback(
    (id, reponse, texte) =>
      monEspaceApi.repondSignal(id, reponse, texte).then((s) => {
        setListe((p) =>
          p.map((x) =>
            x.id === id ||
            (s.referenceId != null && x.referenceId === s.referenceId && /^RDV/.test(x.type))
              ? {
                  ...x,
                  reponse: s.reponse,
                  reponseTexte: s.reponseTexte,
                  reponduLe: s.reponduLe,
                  vuLe: x.vuLe || s.vuLe,
                }
              : x,
          ),
        );
        return s;
      }),
    [],
  );

  return {
    liste,
    nonVus: liste.filter((s) => !s.vuLe).length,
    relit,
    marqueVu,
    marqueTous,
    repond,
  };
}
